import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
    // Actions
    userActions,
    dashboardActions,
    cartActions,
    orderActions,
    pubsubActions,
    membershipActions,

    // Selectors
    selectCartTotals,
    selectEligiblePromos,
    selectPromoDiscount,
    selectRequestedPromos,
    selectDiscountPct,
} from '@redux'

export const useUserRedux = () => {
    const dispatch = useDispatch()
    const userState = useSelector(state => state.user)

    const actions = useMemo(() => {
        return {
            clearUser: () => dispatch(userActions.clearUser()),
            fetchProfile: token => dispatch(userActions.fetchProfile(token)),
            setDiningMethod: method => dispatch(userActions.setDiningMethod(method)),
            setTableId: id => dispatch(userActions.setTableId(id)),
            setPickupDate: date => dispatch(userActions.setPickupDate(date)),
            setPickupTime: time => dispatch(userActions.setPickupTime(time)),
            setTargetStallId: id => dispatch(userActions.setTargetStallId(id)),
            clearTargetStallId: () => dispatch(userActions.clearTargetStallId()),
        }
    }, [dispatch])

    return {
        ...userState,
        ...actions,
    }
}

export const useDashboardRedux = () => {
    const dispatch = useDispatch()
    const dashboardState = useSelector(state => state.dashboard)

    const actions = useMemo(() => {
        return {
            fetchMenus: () => dispatch(dashboardActions.fetchMenus()),
            fetchCafeConfig: () => dispatch(dashboardActions.fetchCafeConfig()),
            fetchCafeCashRegisters: () => dispatch(dashboardActions.fetchCafeCashRegisters()),
            fetchCafeGroups: () => dispatch(dashboardActions.fetchCafeGroups()),
            setSelectedCategory: category => dispatch(dashboardActions.setSelectedCategory(category)),
            setSelectedProduct: product => dispatch(dashboardActions.setSelectedProduct(product)),
            setSearchValue: value => dispatch(dashboardActions.setSearchValue(value)),
            setSelectedGroupId: id => dispatch(dashboardActions.setSelectedGroupId(id)),
        }
    }, [dispatch])

    return {
        ...dashboardState,
        ...actions,
    }
}

export const useCartRedux = () => {
    const dispatch = useDispatch()
    const cartState = useSelector(state => state.cart)

    const actions = useMemo(() => {
        return {
            addItem: item => dispatch(cartActions.addItem(item)),
            replaceItem: () => dispatch(cartActions.replaceItem()),
            cancelReplace: () => dispatch(cartActions.cancelReplace()),
            removeItem: idx => dispatch(cartActions.removeItem(idx)),
            removeVariant: (idx, variantId) => dispatch(cartActions.removeVariant({ idx, variantId })),
            clearCart: () => dispatch(cartActions.clearCart()),
            plusQuantity: (idx, variantId) => dispatch(cartActions.plusQuantity({ idx, variantId })),
            minusQuantity: (idx, variantId) => dispatch(cartActions.minusQuantity({ idx, variantId })),
            checkInventoryStatus: ids => dispatch(cartActions.checkInventoryStatus(ids)),
            initClientRequestId: () => dispatch(cartActions.initClientRequestId()),
            clearClientRequestId: () => dispatch(cartActions.clearClientRequestId()),
            clearAssistantData: () => dispatch(cartActions.clearAssistantData()),
            setCreditToUseCents: cents => dispatch(cartActions.setCreditToUseCents(cents)),
            resetCredit: () => dispatch(cartActions.resetCredit()),
            setIsComplimentaryOrder: isComplimentary => dispatch(cartActions.setIsComplimentaryOrder(isComplimentary)),
            setComplimentaryOrderReason: reason => dispatch(cartActions.setComplimentaryOrderReason(reason)),
        }
    }, [dispatch])

    return {
        ...cartState,
        ...actions,
    }
}

export const useOrderRedux = () => {
    const dispatch = useDispatch()
    const orderState = useSelector(state => state.order)

    const actions = useMemo(() => {
        return {
            setCompleteOrder: order => dispatch(orderActions.setCompleteOrder(order)),
            setSearchedOrder: order => dispatch(orderActions.setSearchedOrder(order)),
            submitOrder: input => dispatch(orderActions.submitOrder(input)),
            submitMemberOrder: input => dispatch(orderActions.submitMemberOrder(input)),
            fetchOrderByNumber: input => dispatch(orderActions.fetchOrderByNumber(input)),
            fetchOrderById: orderId => dispatch(orderActions.fetchOrderById(orderId)),
            cancelProduct: productId => dispatch(orderActions.cancelProduct(productId)),
            fetchAvailablePromos: () => dispatch(orderActions.fetchAvailablePromos()),
            updatePromoCheckTimestamp: () => dispatch(orderActions.updatePromoCheckTimestamp()),
        }
    }, [dispatch])

    return {
        ...orderState,
        ...actions,
    }
}

export const usePubsubRedux = () => {
    const dispatch = useDispatch()
    const pubsubState = useSelector(state => state.pubsub)

    const actions = useMemo(() => {
        return {
            setPubsubStatus: s => dispatch(pubsubActions.setStatus(s)),
            setPubsubError: e => dispatch(pubsubActions.setError(e)),
            setActiveGroupId: id => dispatch(pubsubActions.setActiveGroupId(id)),
            clearActiveGroupId: () => dispatch(pubsubActions.clearActiveGroupId()),
        }
    }, [dispatch])

    return {
        ...pubsubState,
        ...actions,
    }
}

export const useMembershipRedux = () => {
    const dispatch = useDispatch()
    const membershipState = useSelector(state => state.membership)

    const actions = useMemo(() => {
        return {
            // Sync Actions
            setLoading: loading => dispatch(membershipActions.setLoading(loading)),
            setError: error => dispatch(membershipActions.setError(error)),
            clearError: () => dispatch(membershipActions.clearError()),

            setMemberData: data => dispatch(membershipActions.setMemberData(data)),
            clearMemberData: () => dispatch(membershipActions.clearMemberData()),
            updateCredit: credit => dispatch(membershipActions.updateCredit(credit)),
            updatePoints: points => dispatch(membershipActions.updatePoints(points)),

            setEmailVerificationData: data => dispatch(membershipActions.setEmailVerificationData(data)),
            clearEmailVerificationData: () => dispatch(membershipActions.clearEmailVerificationData()),
            setEmailVerified: verified => dispatch(membershipActions.setEmailVerified(verified)),

            setOrderHistory: history => dispatch(membershipActions.setOrderHistory(history)),
            addOrderToHistory: order => dispatch(membershipActions.addOrderToHistory(order)),
            setOrderHistoryLoading: loading => dispatch(membershipActions.setOrderHistoryLoading(loading)),
            setOrderHistoryError: error => dispatch(membershipActions.setOrderHistoryError(error)),

            logout: () => dispatch(membershipActions.logout()),

            // Async Actions
            registerMember: input => dispatch(membershipActions.registerMemberAsync(input)),
            requireOTP: input => dispatch(membershipActions.requireOTPAsync(input)),
            verifyEmailOTP: input => dispatch(membershipActions.verifyEmailOTPAsync(input)),
            getMemberAccessToken: input => dispatch(membershipActions.getMemberAccessTokenAsync(input)),
            refreshAccessToken: input => dispatch(membershipActions.refreshAccessTokenAsync(input)),
            revokeRefreshToken: input => dispatch(membershipActions.revokeRefreshTokenAsync(input)),
            getCurrentMember: () => dispatch(membershipActions.getCurrentMemberAsync()),
            retrieveMemberOrderHistory: input => dispatch(membershipActions.retrieveMemberOrderHistoryAsync(input)),
            retrieveMemberUpcomingOrders: input => dispatch(membershipActions.retrieveMemberUpcomingOrdersAsync(input)),
            checkPendingRatings: () => dispatch(membershipActions.checkPendingRatingsAsync()),
            submitDishRatings: input => dispatch(membershipActions.submitDishRatingsAsync(input)),
            cancelMemberOrder: input => dispatch(membershipActions.cancelMemberOrderAsync(input)),
        }
    }, [dispatch])

    return {
        ...membershipState,
        ...actions,
    }
}

/**
 * Hook for cart totals and promo-related derived values
 * Uses memoized selectors - only recalculates when dependencies change
 */
export const useCartTotals = () => {
    const totals = useSelector(selectCartTotals)
    const eligiblePromos = useSelector(selectEligiblePromos)
    const promoDiscount = useSelector(selectPromoDiscount)
    const requestedPromos = useSelector(selectRequestedPromos)
    const discountPct = useSelector(selectDiscountPct)

    return {
        totals,
        eligiblePromos,
        promoDiscount,
        requestedPromos,
        discountPct,
    }
}
